import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import _ from 'lodash';

import classNames from 'classnames';

import Image from '@nerdwallet/react-image';
import { Title, Box, Text, Accent } from '@nerdwallet/currency';

import {
  stringInputSpec,
  titleInput,
  stringSelectSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';

import './image-content.less';
import SpotifyPlayer from '../../lib/spotify-player';
import SoundCloudPlayer from '../../lib/soundcloud-player';
import YouTubePlayer from '../../lib/youtube-player/youtube-player';

const titleSizeEnum = {
  'x-small': 'default',
  small: 'default',
  medium: 'large',
  large: 'small',
  'x-large': 'default',
};

const outputMediaTypeValues = {
  IMAGE: 'image',
  VIDEO: 'video',
  SPOTIFY: 'spotify',
  SOUNDCLOUD: 'soundcloud',
  YOUTUBE: 'youtube',
};

const ImageContentBox = ({
  includeVideo: includeVideoLegacy,
  outputMediaType,
  spotifyUrl,
  soundCloudEpisodeId,
  podcastMediaHight,
  podcastMediaWidth,
  soundCloudPlayerColor,
  playButtonImage,
  youtubeVideoSlug,
  youtubeImagePlaceholder,
  youtubeMaxHeight,
  youtubeMaxWidth,
  videoUrl,
  videoAutoplay,
  videoControls,
  videoLoop,
  videoMuted,
  includeVideoMobileSrc,
  videoMobileSrc,
  videoTrack,
  videoTrackKind,
  videoMaxHeightDesktop,
  videoMaxHeightMobile,
  videoMaxHeightTablet,
  imageUrl,
  tabletImageUrl,
  mobileImageUrl,
  imageAlt,
  title,
  titleSize,
  dangerouslyAllowHTMLInTitle,
  body,
  bodySize,
  bodyInBold,
  centerText,
  dangerouslyAllowHTMLInBody,
  size,
}) => {
  const contentAlign = centerText ? 'centered' : 'left-aligned';
  // const includeVideo = outputMediaType === 'video' || legacyVideoMode;
  const includeVideo = outputMediaType === 'video' || includeVideoLegacy;
  const processedTitle = dangerouslyAllowHTMLInTitle ? (
    // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
    <span dangerouslySetInnerHTML={{ __html: title }} />
  ) : (
    title
  );

  const processedBody = dangerouslyAllowHTMLInBody ? (
    // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
    <span dangerouslySetInnerHTML={{ __html: body }} />
  ) : (
    body
  );

  const imageSizeMap = {
    lg: imageUrl,
    md: tabletImageUrl,
    sm: mobileImageUrl,
  };

  const imageSrc = size && !!imageSizeMap[size] ? imageSizeMap[size] : imageUrl;

  const videoMaxHeightMap = {
    lg: videoMaxHeightDesktop,
    md: videoMaxHeightTablet,
    sm: videoMaxHeightMobile,
  };

  const videoMaxHeight =
    size && !!videoMaxHeightMap[size] ? videoMaxHeightMap[size] : null;

  const videoMarkup = (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      className={classNames(['image-content__vid'])}
      width="100%"
      height="100%"
      autoPlay={videoAutoplay}
      controls={videoControls}
      muted={videoMuted}
      loop={videoLoop}
      style={{
        maxHeight: `${videoMaxHeight}`,
      }}
    >
      <source src={videoUrl} type="video/mp4" />
      {videoTrack && (
        <track
          src={videoTrack}
          kind={videoTrackKind}
          srcLang="en"
          label="English"
        />
      )}
    </video>
  );

  let TitleComponent = Text;

  if (titleSize === 'large' || titleSize === 'x-large') {
    TitleComponent = Title;
  }

  if (titleSize === 'x-small') {
    TitleComponent = Accent;
  }

  return (
    <Box className={classNames(['image-content__block'], `${contentAlign}`)}>
      {includeVideo && videoUrl && size !== 'sm' && videoMarkup}
      {includeVideo &&
        includeVideoMobileSrc &&
        videoMobileSrc &&
        size === 'sm' && (
          <Image
            className={classNames(['image-content__sm-vid'])}
            src={videoMobileSrc}
            objectFit="contain"
            objectPosition="center"
          />
        )}
      {includeVideo && !includeVideoMobileSrc && size === 'sm' && videoMarkup}
      {!includeVideo &&
        imageUrl &&
        outputMediaType === outputMediaTypeValues.IMAGE && (
          <Image
            className={classNames(['image-content__img'])}
            src={imageSrc}
            objectFit="cover"
            objectPosition="center"
            alt={imageAlt}
          />
        )}
      {outputMediaType === outputMediaTypeValues.SPOTIFY && (
        <div className={classNames(['image-content__vid'])}>
          <SpotifyPlayer
            spotifyEpisodeUrl={spotifyUrl}
            podcastMediaHight={podcastMediaHight}
            podcastMediaWidth={podcastMediaWidth}
          />
        </div>
      )}
      {outputMediaType === outputMediaTypeValues.SOUNDCLOUD && (
        <div className={classNames(['image-content__vid'])}>
          <SoundCloudPlayer
            soundCloudEpisodeId={soundCloudEpisodeId}
            podcastMediaHight={podcastMediaHight}
            podcastMediaWidth={podcastMediaWidth}
            soundCloudPlayerColor={soundCloudPlayerColor}
          />
        </div>
      )}
      {outputMediaType === outputMediaTypeValues.YOUTUBE && (
        <div className={classNames(['image-content__vid'])}>
          <YouTubePlayer
            youtubePlaceholderImageUrl={youtubeImagePlaceholder}
            youtubePlayButtonImageUrl={playButtonImage}
            youtubeMaxHeight={youtubeMaxHeight}
            youtubeMaxWidth={youtubeMaxWidth}
            youtubeVideoSlug={youtubeVideoSlug}
          />
        </div>
      )}
      {title && title !== '' && (
        <Box className={classNames(['image-content__hdr'])}>
          <TitleComponent size={titleSizeEnum[titleSize]}>
            {processedTitle}
          </TitleComponent>
        </Box>
      )}
      {body && body !== '' && (
        <Box className={classNames(['image-content__body'])}>
          <Text size={bodySize} bold={bodyInBold}>
            {processedBody}
          </Text>
        </Box>
      )}
    </Box>
  );
};

ImageContentBox.propTypes = {
  outputMediaType: PropTypes.oneOf(Object.values(outputMediaTypeValues)),
  includeVideo: PropTypes.bool,
  videoAutoplay: PropTypes.bool,
  videoControls: PropTypes.bool,
  videoLoop: PropTypes.bool,
  includeVideoMobileSrc: PropTypes.bool,
  videoMobileSrc: PropTypes.string,
  videoMuted: PropTypes.bool,
  videoTrackKind: PropTypes.string,
  videoTrack: PropTypes.string,
  videoUrl: PropTypes.string,
  videoMaxHeightDesktop: PropTypes.string,
  videoMaxHeightMobile: PropTypes.string,
  videoMaxHeightTablet: PropTypes.string,
  imageUrl: PropTypes.string,
  tabletImageUrl: PropTypes.string,
  mobileImageUrl: PropTypes.string,
  imageAlt: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.string,
  dangerouslyAllowHTMLInTitle: PropTypes.bool,
  body: PropTypes.string,
  bodySize: PropTypes.string,
  bodyInBold: PropTypes.bool,
  centerText: PropTypes.bool,
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  size: PropTypes.string,
  spotifyUrl: PropTypes.string,
  soundCloudEpisodeId: PropTypes.string,
  podcastMediaHight: PropTypes.string,
  podcastMediaWidth: PropTypes.string,
  soundCloudPlayerColor: PropTypes.string,
  playButtonImage: PropTypes.string,
  youtubeVideoSlug: PropTypes.string,
  youtubeImagePlaceholder: PropTypes.string,
  youtubeMaxHeight: PropTypes.string,
  youtubeMaxWidth: PropTypes.string,
};

ImageContentBox.defaultProps = {
  outputMediaType: 'image',
  videoAutoplay: false,
  videoControls: false,
  videoLoop: false,
  includeVideoMobileSrc: false,
  videoMobileSrc: '',
  videoMuted: false,
  videoTrackKind: 'subtitles',
  videoTrack: '',
  videoUrl: '',
  videoMaxHeightDesktop: '',
  videoMaxHeightTablet: '',
  videoMaxHeightMobile: '',
  imageUrl: '',
  tabletImageUrl: '',
  mobileImageUrl: '',
  imageAlt: '',
  title: 'Title text',
  titleSize: 'medium',
  dangerouslyAllowHTMLInTitle: false,
  body: 'body text body text body text',
  bodySize: 'medium',
  bodyInBold: false,
  dangerouslyAllowHTMLInBody: false,
  size: '',
  spotifyUrl: '',
  soundCloudEpisodeId: '',
  podcastMediaHight: '',
  podcastMediaWidth: '',
  soundCloudPlayerColor: '',
  playButtonImage: '',
  youtubeVideoSlug: '',
  youtubeMaxHeight: '',
  youtubeMaxWidth: '',
  youtubeImagePlaceholder:
    'https://www.nerdwallet.com/cdn/landing/NW_LPC_generic-thumbnail.png',
};

const imageOrMediaOutput = (componentDocumentProps) => {
  const outputMobileImageNotVideo =
    _.get(componentDocumentProps, 'includeVideoMobileSrc', '') === true
      ? [
          stringInputSpec(
            'videoMobileSrc',
            'URL of image to display for mobile viewports only',
            {
              help: 'If you specify a file path here to a source image, the image will be displayed in mobile viewports instead of the video. The video will always be displayed in tablet and desktop viewports.',
            }
          ),
        ]
      : '';

  const includeVideoLegacy = _.get(
    componentDocumentProps,
    'includeVideo',
    false
  );
  if (includeVideoLegacy) {
    _.set(componentDocumentProps, 'outputMediaType', 'video');
    _.set(componentDocumentProps, 'includeVideo', false);
  }
  const type = _.get(componentDocumentProps, 'outputMediaType', 'image');
  const podcastDimensionFields = [
    stringInputSpec('podcastMediaHight', 'Podcast height', {
      help: 'The podcast frame height in pixels. Default value is 232px',
    }),
    stringInputSpec('podcastMediaWidth', 'Podcast width', {
      help: 'The podcast frame width in pixels or percentage. Default value is 100%',
    }),
  ];
  const result = {
    image: [
      stringInputSpec('imageUrl', 'Desktop image URL'),
      stringInputSpec('tabletImageUrl', 'Tablet image URL'),
      stringInputSpec('mobileImageUrl', 'Mobile image URL'),
      stringInputSpec('imageAlt', 'Image alt tag'),
    ],
    spotify: [
      stringInputSpec('spotifyUrl', 'Podcast episode URL'),
      {
        help: 'The desired Spotify podcast episode URL',
      },
      ...podcastDimensionFields,
    ],
    youtube: [
      stringInputSpec('youtubeVideoSlug', 'Video Slug', {
        help: 'The Video Slug is the string of text/numerals that immediately follows the https://youtu.be/ portion of the Share URL generated by YouTube.',
      }),
      stringInputSpec('youtubeImagePlaceholder', 'Video image placeholder', {
        help: 'Video preview image output upon page load',
      }),
      stringInputSpec('playButtonImage', 'Play button image', {
        help: 'Image output as the play button',
      }),
      stringInputSpec('youtubeMaxHeight', 'Player max height', {
        help: 'The player frame max height in pixels or percentage. Default value is 232px',
      }),
      stringInputSpec('youtubeMaxWidth', 'Player max width', {
        help: 'The player frame max width in pixels or percentage. Default value is 100%',
      }),
    ],
    soundcloud: [
      stringInputSpec('soundCloudEpisodeId', 'Podcast episode ID', {
        help: 'The podcast episode ID is the string of numerals that immediately follows the XXXXXX portion of the Share URL generated by SoundCloud',
      }),

      ...podcastDimensionFields,
      stringInputSpec('soundCloudPlayerColor', 'Play Button Color', {
        help: 'Color play button and other controls. e.g. "0066CC"',
      }),
    ],
    video: [
      stringInputSpec('videoUrl', 'MP4 video URL'),
      boolCheckboxSpec(
        'videoAutoplay',
        'Autoplay video on page load? (note: autoplay will not occur on mobile devices)',
        {
          help: 'WARNING: We advise against making use of this feature unless you really need it.',
        }
      ),
      boolCheckboxSpec('videoControls', 'Show video controls?'),
      boolCheckboxSpec('videoLoop', 'Play video on loop?'),
      boolCheckboxSpec('videoMuted', 'Mute video audio?'),
      boolCheckboxSpec(
        'includeVideoMobileSrc',
        'Output image for mobile viewports only?',
        {
          help: 'If you wish to display an image instead of the video for mobile viewports only, select this box and provide a URL to the image in the designated field.',
        }
      ),
      ...outputMobileImageNotVideo,
      stringInputSpec('videoTrack', 'MP4 video text track URL', {
        help: 'Specify the URL for a text track associated with this video. Please note all text tracks will default to English.',
      }),
      stringSelectSpec(
        'videoTrackKind',
        'Track kind',
        [
          {
            value: 'subtitles',
            label: 'subtitles',
          },
          {
            value: 'captions',
            label: 'captions',
          },
          {
            value: 'chapters',
            label: 'chapters',
          },
          {
            value: 'descriptions',
            label: 'descriptions',
          },
          {
            value: 'metadata',
            label: 'metadata',
          },
        ],
        {
          help: 'Specifies the kind of text track',
        }
      ),
      stringInputSpec(
        'videoMaxHeightDesktop',
        'Desktop: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
        {
          help: 'Specify the maximum height of the video player viewed in desktop viewports, in pixels. eg: 200px',
        }
      ),
      stringInputSpec(
        'videoMaxHeightTablet',
        'Tablet: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
        {
          help: 'Specify the maximum height of the video player viewed in tablet viewports, in pixels. eg: 200px',
        }
      ),
      stringInputSpec(
        'videoMaxHeightMobile',
        'Mobile: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
        {
          help: 'Specify the maximum height of the video player viewed in mobile viewports, in pixels. eg: 200px',
        }
      ),
    ],
  };
  if (result[type]) {
    return [
      stringSelectSpec(
        'outputMediaType',
        'Output Media Type',
        [
          {
            value: outputMediaTypeValues.IMAGE,
            label: 'Image',
          },
          {
            value: outputMediaTypeValues.VIDEO,
            label: 'Video',
          },
          {
            value: outputMediaTypeValues.YOUTUBE,
            label: 'YouTube',
          },
          {
            value: outputMediaTypeValues.SPOTIFY,
            label: 'Spotify',
          },
          {
            value: outputMediaTypeValues.SOUNDCLOUD,
            label: 'SoundCloud',
          },
        ],
        {
          help: 'The type of media that will be output',
        }
      ),
      ...result[type],
    ];
  }
  return [];
};

ImageContentBox.getEditingConfig = (componentDocument) => {
  return [
    titleInput,
    stringSelectSpec(
      'titleSize',
      'Title size',
      [
        {
          value: 'medium',
          label: 'Medium (default)',
        },
        {
          value: 'x-large',
          label: 'X-large',
        },
        {
          value: 'large',
          label: 'Large',
        },
        {
          value: 'small',
          label: 'Small',
        },
        {
          value: 'x-small',
          label: 'X-Small',
        },
      ],
      {
        help: 'Title size',
      }
    ),
    boolCheckboxSpec(
      'dangerouslyAllowHTMLInTitle',
      'Allow HTML in title text?',
      {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }
    ),
    stringTextAreaSpec('body', 'Body text'),
    stringSelectSpec(
      'bodySize',
      'Body text size',
      [
        {
          value: 'medium',
          label: 'Medium',
        },
        {
          value: 'large',
          label: 'Large',
        },
        {
          value: 'small',
          label: 'Small',
        },
      ],
      {
        help: 'Body text size',
      }
    ),
    boolCheckboxSpec('bodyInBold', 'Body text in bold?'),
    boolCheckboxSpec('dangerouslyAllowHTMLInBody', 'Allow HTML in body text?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    ...imageOrMediaOutput(componentDocument),
  ];
};

ImageContentBox.displayName = 'ImageContentBox';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ImageContentBox);
